import { 
  useGenerateGiftCodesMutation, 
  useCheckInvitationMutation, 
  useAcceptInvitationMutation, 
  useDeleteInviteMutation, 
  useResendInviteMutation, 
  useSendInvitationsMutation,
  invitesApi
} from '../invitesApi';
import { enqueueSnackbar } from 'notistack';
import { TOAST_ERROR, TOAST_SUCCESS } from '../../../utils/constants';
import i18next from 'i18next';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

export const useInvitesActions = () => {
  const dispatch = useDispatch();
  const [generateGiftCodesTrigger] = useGenerateGiftCodesMutation();
  const [checkInvitationTrigger] = useCheckInvitationMutation();
  const [acceptInvitationTrigger] = useAcceptInvitationMutation();
  const [deleteInviteTrigger] = useDeleteInviteMutation();
  const [resendInviteTrigger] = useResendInviteMutation();
  const [sendInvitationsTrigger] = useSendInvitationsMutation();

  const generateGiftCodes = async ({ amount, courseId, objectType, year }) => {
    try {
      const result = await generateGiftCodesTrigger({ amount, courseId, objectType, year }).unwrap();
      enqueueSnackbar(i18next.t('invites.giftCodesGenerated'), { variant: TOAST_SUCCESS });
      return result;
    } catch (err) {
      const errorMessage = err?.error?.response?.errMsg || 'Failed to generate gift codes';
      enqueueSnackbar(errorMessage, { variant: TOAST_ERROR });
      throw err;
    }
  };

  const checkInvitation = useCallback(async ({ hash, giftCode }) => {
    try {
      const result = await checkInvitationTrigger({ hash, giftCode }).unwrap();
      
      // Save the invitation data to modalState
      if (result && result.length > 0) {
        const invitationData = result[0];
        dispatch(invitesApi.util.updateQueryData('invitesModalState', undefined, (draft) => {
          draft.redeemInvitation = invitationData;
          draft.checkStatus = 'succeeded';
          draft.giftCodeError = null;
        }));
      }
      
      return result;
    } catch (err) {
      // Handle different error structures from RTK Query
      // err.data.errMsg is the standard structure from fetchBaseQuery
      // err.error?.data?.errMsg is for nested errors
      const errorMessage = err?.data?.errMsg || err?.error?.data?.errMsg || err?.message || i18next.t('invites.invitationNotFound');

      // Update modalState with error and clear any previous invitation data
      dispatch(invitesApi.util.updateQueryData('invitesModalState', undefined, (draft) => {
        draft.checkStatus = 'failed';
        draft.giftCodeError = errorMessage;
        draft.redeemInvitation = {};
      }));
      // Let the caller decide how to surface this (inline form error, etc.)
      throw err;
    }
  }, [checkInvitationTrigger, dispatch]);

  const acceptInvitation = useCallback(async ({ hash, giftCode }) => {
    try {
      const result = await acceptInvitationTrigger({ hash, giftCode }).unwrap();
      enqueueSnackbar(i18next.t('invites.invitationAccepted'), { variant: TOAST_SUCCESS });
      return result;
    } catch (err) {
      const errorMessage = err?.error?.response?.errMsg || 'Failed to accept invitation';
      enqueueSnackbar(errorMessage, { variant: TOAST_ERROR });
      throw err;
    }
  }, [acceptInvitationTrigger]);

  const deleteInvite = async (id) => {
    try {
      const result = await deleteInviteTrigger(id).unwrap();
      enqueueSnackbar(i18next.t('invites.inviteDeleted'), { variant: TOAST_SUCCESS });
      return result;
    } catch (err) {
      const errorMessage = err?.error?.response?.errMsg || 'Failed to delete invite';
      enqueueSnackbar(errorMessage, { variant: TOAST_ERROR });
      throw err;
    }
  };

  const resendInvite = async (id) => {
    try {
      const result = await resendInviteTrigger(id).unwrap();
      enqueueSnackbar(i18next.t('invites.inviteResent'), { variant: TOAST_SUCCESS });
      return result;
    } catch (err) {
      const errorMessage = err?.error?.response?.errMsg || 'Failed to resend invite';
      enqueueSnackbar(errorMessage, { variant: TOAST_ERROR });
      throw err;
    }
  };

  const sendInvitations = async ({ byWhat, expires, courseIds, withWhat, circleId }) => {
    try {
      const result = await sendInvitationsTrigger({ byWhat, expires, courseIds, withWhat, circleId }).unwrap();
      enqueueSnackbar(i18next.t('invites.invitationsSent'), { variant: TOAST_SUCCESS });
      return result;
    } catch (err) {
      const errorMessage = err?.error?.response?.errMsg || 'Failed to send invitations';
      enqueueSnackbar(errorMessage, { variant: TOAST_ERROR });
      throw err;
    }
  };

  // UI modal actions using RTKQ cache (replace direct util calls in components)
  const openInvitesModal = useCallback((modalProps = {}) => {
    dispatch(invitesApi.util.updateQueryData('invitesModalState', undefined, (draft) => {
      if (!draft) return;
      draft.isInviteModalOpen = true;
      draft.modalProps = modalProps;
    }));
  }, [dispatch]);

  const closeInvitesModal = useCallback(() => {
    dispatch(invitesApi.util.updateQueryData('invitesModalState', undefined, (draft) => {
      if (!draft) return;
      draft.isInviteModalOpen = false;
      draft.modalProps = {};
    }));
  }, [dispatch]);

  const setInvitesModalProps = useCallback((modalProps = {}) => {
    dispatch(invitesApi.util.updateQueryData('invitesModalState', undefined, (draft) => {
      if (!draft) return;
      draft.modalProps = modalProps;
    }));
  }, [dispatch]);

  const setPendingGiftCode = useCallback((code) => {
    dispatch(invitesApi.util.updateQueryData('invitesModalState', undefined, (draft) => {
      if (!draft) return;
      draft.pendingGiftCode = code || null;
    }));
  }, [dispatch]);

  return {
    generateGiftCodes,
    checkInvitation,
    acceptInvitation,
    deleteInvite,
    resendInvite,
    sendInvitations,
    openInvitesModal,
    closeInvitesModal,
    setInvitesModalProps,
    setPendingGiftCode,
  };
};
